import type { Period } from '@/entities/types';
import {
  getQuarterStartAt,
  getWeekStartAt,
  getYearStartAt,
  toMidnightLocalString,
} from '@/utils/datetime';
import { useCompletePlan, type CompletePlan } from './useCompletePlan';

export interface ParentPlanCandidate {
  completePlan: CompletePlan | undefined;
  period: Period;
  startAt: Date;
  startAtLocal: string;
}

export function findParentContextPlan(
  candidates: ParentPlanCandidate[],
): ParentPlanCandidate | undefined {
  if (candidates.length === 0) {
    return undefined;
  }

  const withGoals = candidates.find(
    (c) =>
      c.completePlan !== undefined &&
      c.completePlan.goals.some((g) => g.title.trim() !== ''),
  );

  return withGoals ?? candidates[0];
}

export function useParentContextPlan(
  period: Period,
  targetStartAt: Date,
): { parentContext: ParentPlanCandidate | undefined; isLoading: boolean } {
  const weekStartAt = getWeekStartAt(targetStartAt);
  const weekStartAtLocal = toMidnightLocalString(weekStartAt);
  const { completePlan: weekPlan, isLoading: weekPlanLoading } =
    useCompletePlan('week', weekStartAt, weekStartAtLocal);

  const quarterStartAt = getQuarterStartAt(targetStartAt);
  const quarterStartAtLocal = toMidnightLocalString(quarterStartAt);
  const { completePlan: quarterPlan, isLoading: quarterPlanLoading } =
    useCompletePlan('quarter', quarterStartAt, quarterStartAtLocal);

  const yearStartAt = getYearStartAt(targetStartAt);
  const yearStartAtLocal = toMidnightLocalString(yearStartAt);
  const { completePlan: yearPlan, isLoading: yearPlanLoading } =
    useCompletePlan('year', yearStartAt, yearStartAtLocal);

  const allCandidates: ParentPlanCandidate[] = [
    {
      completePlan: weekPlan,
      period: 'week',
      startAt: weekStartAt,
      startAtLocal: weekStartAtLocal,
    },
    {
      completePlan: quarterPlan,
      period: 'quarter',
      startAt: quarterStartAt,
      startAtLocal: quarterStartAtLocal,
    },
    {
      completePlan: yearPlan,
      period: 'year',
      startAt: yearStartAt,
      startAtLocal: yearStartAtLocal,
    },
  ];

  let candidates: ParentPlanCandidate[];
  if (period === 'day') {
    candidates = allCandidates;
  } else if (period === 'week') {
    candidates = allCandidates.slice(1);
  } else if (period === 'quarter') {
    candidates = allCandidates.slice(2);
  } else {
    candidates = [];
  }

  const parentContext = findParentContextPlan(candidates);
  const isLoading = weekPlanLoading || quarterPlanLoading || yearPlanLoading;

  return { parentContext, isLoading };
}
